<template>
  <Loading v-if="componentLoading" :style="tableStyle" />
  <template v-else>
    <view
      :class="{
        'pft-table-component': true,
        'no-header': !showHeader,
      }"
      ref="tableRef"
      :style="tableStyle"
    >
      <view class="pft-table-component__header" v-if="showHeader">
        <template v-for="(column, index) in headerCol" :key="column.key">
          <template v-if="column.subColumns?.length > 0">
            <view class="pft-table-component__header__item" :class="headerClass(index)" :style="headerStyle(column, 1, index)">
              {{ column.title }}
            </view>

            <view
              v-for="subCol of column.subColumns"
              :key="subCol.key"
              class="pft-table-component__header__item"
              :class="headerClass(index)"
              :style="headerStyle(column, 2)"
            >
              {{ subCol.title }}
            </view>
          </template>
          <template v-else>
            <view class="pft-table-component__header__item" :class="headerClass(index)" :style="headerStyle(column)">
              {{ column.title }}
            </view>
          </template>
        </template>
      </view>
      <view v-if="data.length === 0" class="table-empty" :style="emptyTableBodyStyle">
        <view>暂无数据</view>
      </view>
      <template v-else>
        <PlainList
          v-if="plainList"
          :data="data"
          :isCompare="compareData.length > 0"
          :columns="columns"
          :theme="theme"
          :separated="separated"
          :rows="rows"
        />
        <template v-else-if="scrollType === 'page'">
          <PageScroll
            :data="data"
            :duration="duration"
            :isCompare="compareData.length > 0"
            :columns="columns"
            :theme="theme"
            :separated="separated"
          />
        </template>
        <template v-else>
          <LineScroll :columns="columns" :separated="separated" :rows="rows" :data="data" :isCompare="compareData.length > 0" :duration="duration" />
        </template>
      </template>
    </view>
  </template>
</template>
<script setup lang="tsx">
import { computed, onUnmounted, ref, watch, nextTick, provide } from "vue"
import numeral from "numeral"
import { defaultsDeep, cloneDeep } from "lodash-es"
import Loading from "@p-v/packages/Loading.vue"
import crown1 from "@p-v/asset/image/icon_top1_gold.png"
import crown2 from "@p-v/asset/image/icon_top_2.png"
import crown3 from "@p-v/asset/image/icon_top3.png"
import { JSX } from "vue/jsx-runtime"
import { jsonToCss } from "@p-v/utils/cssJson"

import PlainList from "./theme/plainList.vue"
import LineScroll from "./theme/lineScroll.vue"
import PageScroll from "./theme/pageScroll.vue"

const icon = {
  crown1,
  crown2,
  crown3,
}

provide("icon", icon)

defineOptions({
  name: "PftTable",
})
interface ColumnItem {
  key: string
  titleStyle?: { [key: string]: string; title: string }
  style: { [key: string]: string; title: string }
  rankType?: string
  tpl?: string
  format?: string | ((val: string | number) => string)
  align?: string
  listStyle?: string
  width?: string
  type?: string
  title: string
  subColumns: ColumnItem[]
  startIndex?: number //记录当前列的起始位置
}

const stackRows = ref(1)

interface SumItem {
  type: "text" | "sum"
  text?: string
  key?: string
  format?: string | ((val: string | number) => string)
  style?: { [key: string]: string }
}

interface DataTextProps {
  showHeader?: boolean
  separated?: boolean
  rows?: number
  duration?: number
  theme?: "default" | "data-percent" | "crown123"
  style?: { [key: string]: string }
  sum?: SumItem[]
  plainList?: boolean
  scrollType?: "line" | "page"
  rowStyle?: { [key: string]: string }
  seperatedStyle?: { [key: string]: string }
  showBoard?: boolean
  boardColor?: string
  separatedColor?: string
  compareStartColIndex?: number
  compareRule?: (row: any, compareData: any) => boolean
}

const props = withDefaults(defineProps<DataTextProps>(), {
  showHeader: () => true,
  separated: () => false,
  rows: () => 5,
  duration: () => 5,
  theme: () => "default",
  style: () => ({}),
  rowStyle: () => ({}),
  seperatedStyle: () => ({}),
  sum: () => [],
  plainList: () => false,
  scrollType: () => "line",
  showBoard: () => false,
  boardColor: () => "#333",
  separatedColor: () => "transparent",
  compareStartColIndex: undefined,
  compareRule: undefined,
})

provide("theme", props.theme)
provide("style", props.style)
provide("seperatedStyle", props.seperatedStyle)
provide("rowStyle", props.rowStyle)
provide("separated", props.separated)
provide("compareStartColIndex", props.compareStartColIndex)

const styleVar = computed(() => {
  if (!props.showBoard) {
    return {
      "--content-border": "none",
    }
  }
  return {
    "--content-border": `1px solid ${props.boardColor}`,
  }
})

const activeRow = ref<number | number[]>(0)
const headerCol = ref<ColumnItem[]>([])
const columns = ref<ColumnItem[]>([])
const data = ref<{ [key: string]: string | number }[]>([])
const compareData = ref<{ [key: string]: string | number }[]>([])
const componentLoading = ref<boolean>(true)
const sumConfig = ref<SumItem[]>(props.sum)
const sumWithScroll = ref<boolean>(false)

provide("sumWithScroll", sumWithScroll)
provide("sumConfig", sumConfig)

const rowHeight = ref(32)

const headerHeight = ref(32)

const tableRef = ref<HTMLElement | null>(null)

const formatData = (val: string | number, format: string | ((val: string | number) => string)) => {
  if (val === undefined || val === null || val == 0) {
    if (val == 0) {
      if (typeof format === "function") {
        return format(val)
      }
      return numeral(Number(val)).format(format)
    } else {
      return ""
    }
  }
  if (typeof val === "string" && /\d+/.test(val) === false) {
    return val
  }
  if (typeof format === "function") {
    return format(val)
  }
  return numeral(Number(val)).format(format)
}

provide("formatData", formatData)

const sumColumn = (key: string, format: string | ((val: string | number) => string)) => {
  const total = data.value.reduce((prev, cur) => prev + Number(cur[key] || 0), 0)
  return formatData(total, format)
}
provide("sumColumn", sumColumn)

const getVal = (row: { [key: string]: string | number }, key: string) => {
  if (!key || !row) return "-"
  let obj = cloneDeep(row)
  for (let k of key.split(".")) {
    if (obj[k] != undefined) {
      obj = obj[k] as unknown as { [key: string]: string | number }
    } else {
      return "-"
    }
  }
  return obj as unknown as string | number
}

provide("getVal", getVal)

const compareFn = ref<(row: any, compareRow: any) => boolean>(
  props.compareRule ||
    ((_row, _compareRow) => {
      return false
    }),
)

const getCompareRow = (row: { [key: string]: string | number }) => {
  return compareData.value.find(item => {
    return compareFn.value(row, item)
  })
}
provide("getCompareRow", getCompareRow)

//根据列的索引值返回对应的样式
const headerClass = (num: number) => {
  return {
    "align-left": columns.value[num].align === "left",
    "align-right": columns.value[num].align === "right",
  }
}

const headerStyle = (column: ColumnItem, isStack?: 1 | 2, startCol?: number) => {
  const newStyle = defaultsDeep({}, column.titleStyle, { color: "#afe5ff", fontWeight: "normal", height: "32px" }, column.style)

  if (newStyle.height) {
    newStyle.lineHeight = newStyle.height
    headerHeight.value = Number(newStyle.height.replace("px", ""))
  }
  if (isStack === undefined && stackRows.value > 1) {
    newStyle["grid-row-start"] = "1"
    newStyle["grid-row-end"] = `${stackRows.value + 1}`
    delete newStyle.height
  } else if (isStack === 1) {
    newStyle["grid-row-start"] = "1"
    newStyle["grid-row-end"] = "2"
    if (startCol && startCol >= 0) {
      const columnData = headerCol.value[startCol]
      if (columnData.startIndex) {
        newStyle["grid-column-start"] = `${columnData.startIndex}`
        newStyle["grid-column-end"] = `${columnData.startIndex + columnData.subColumns?.length || 1}`
      }
    }
  } else if (isStack === 2) {
    newStyle["grid-row-start"] = "2"
    newStyle["grid-row-end"] = "3"
  }
  return newStyle
}

provide("style:grid-template-columns", columns.value.map(column => column.width || "1fr").join(" "))

const RowHeight = computed(() => {
  let maxRowHeight = 0
  if (props.rowStyle) {
    const { height } = props.rowStyle
    if (height) {
      maxRowHeight = Number(height.replace("px", ""))
    }
  }

  columns.value.forEach(item => {
    let style = item.style || {}
    if (style instanceof Function) {
      style = style()
    }
    if (style && style.height) {
      maxRowHeight = Math.max(maxRowHeight, Number(style.height.replace("px", "")))
    }
  })
  if (maxRowHeight === 0) {
    maxRowHeight = 32
  }

  if (props.theme == "data-percent") {
    maxRowHeight += 8
  }

  if (props.compareStartColIndex && props.compareStartColIndex > 0) {
    maxRowHeight *= 2
  }

  if (props.rowStyle) {
    const { marginTop, marginBottom, margin } = props.rowStyle
    if (marginTop) {
      maxRowHeight += Number(marginTop.replace("px", ""))
    }
    if (marginBottom) {
      maxRowHeight += Number(marginBottom.replace("px", ""))
    }
    if (margin) {
      // 判断是否有空格
      if (margin.indexOf(" ") > -1) {
        const marginArr = margin.split(" ")
        if (marginArr.length > 1) {
          maxRowHeight += Number(marginArr[0].replace("px", ""))
          maxRowHeight += Number(marginArr[2].replace("px", ""))
        } else {
          maxRowHeight += Number(marginArr[0].replace("px", "")) * 2
        }
      } else {
        maxRowHeight += Number(margin.replace("px", "")) * 2
      }
    }
  }

  return maxRowHeight
})

provide("RowHeight", RowHeight)

const tableStyle = computed(() => {
  let styleBody = {
    ...props.style,
    ...styleVar.value,
  }

  let maxTitleHeight = 0
  let headerHeightRatio = 1
  columns.value.forEach(item => {
    if (item.titleStyle && item.titleStyle.height) {
      maxTitleHeight = Math.max(maxTitleHeight, Number(item.titleStyle.height.replace("px", "")))
    }
    let style = item.style || {}
    if (style instanceof Function) {
      style = style()
    }
    if (item.subColumns) {
      headerHeightRatio = 2
    }
  })
  if (maxTitleHeight === 0) {
    maxTitleHeight = 32 * headerHeightRatio
  }

  styleBody["--SEPARATED-BACKGROUND"] = props.separatedColor
  styleBody["--MAX-ROW-HEIGHT"] = `${RowHeight.value}px`
  styleBody["--MAX-TITLE-HEIGHT"] = `${maxTitleHeight}px`

  styleBody["--GRID-ROW-TEMPLATE-COLUMNS"] = columns.value.map(column => column.width || "1fr").join(" ")
  if (props.theme == "data-percent") {
    styleBody["--GRID-ROW-TEMPLATE-ROWS"] = "1fr 8px"
    styleBody["--MAX-ROW-ALL-HEIGHT"] = `${RowHeight.value + 8}px`
  } else {
    styleBody["--GRID-ROW-TEMPLATE-ROWS"] = "1fr"
    styleBody["--MAX-ROW-ALL-HEIGHT"] = `${RowHeight.value}px`
  }
  return styleBody
})

const emptyTableBodyStyle = computed(() => {
  const { position, left, top, ...other } = props.style
  return {
    ...other,
    // "grid-column-start": 1,
    // "grid-column-end": columns.value.length + 1,
    // "grid-template-columns": columns.value.map(column => column.width || "1fr").join(" "),
  }
})

/**
 * 模板函数，识别模板中{key%|class}格式的字符串，替换为对应的view元素并带上相应的class
 * @param tpl
 * @param key
 * @param val
 */

const renderTpl = (tpl: string, data: { [key: string]: string | number }, style: { [key: string]: string }) => {
  /**
   * 声明特征识别的正则，可识别一下格式的字符串
   * {[key]%|class}
   * {[key]|class1}
   * {[key]}
   */
  const reg = /(\{[^{}]+\})/g
  const match = tpl.match(reg)
  if (!match) {
    return tpl
  } else {
    const jsxObj: { [key: string]: JSX.IntrinsicElements } = {}
    const jsxArr: JSX.IntrinsicElements[] = []
    for (let i of match) {
      const keyTpl = i.slice(1, -1).split("|")[0] //可能是[key]%或者[key]
      const classStr = i.slice(1, -1).split("|")[1]
      //正则识别keyTpl中的key
      const resultMatch = keyTpl.match(/\[([^{}]+)\]/)

      if (resultMatch) {
        const key = resultMatch[1]
        const tplStr = keyTpl.replace(`[${key}]`, getVal(data, key).toString())
        jsxObj[i] = <text class={classStr}>{tplStr}</text>
      }
    }
    const newAr = tpl.split(reg)
    for (let i of newAr) {
      if (jsxObj[i]) {
        jsxArr.push(jsxObj[i])
      } else {
        jsxArr.push(<text>{i}</text>)
      }
    }
    return (
      <view class="pft-text-tpl" style={jsonToCss(style)}>
        {[...jsxArr]}
      </view>
    )
  }
}

provide("renderTpl", renderTpl)
const loading = () => {
  componentLoading.value = true
}

const setColums = (col: ColumnItem[]) => {
  columns.value = col
}

const setActive = (active: number | number[]) => {
  activeRow.value = active
}

const dealColData = (col: ColumnItem[], row: number, parentItem?: ColumnItem) => {
  const colums: ColumnItem[] = []
  let i = 1
  for (let item of col) {
    if (item.subColumns) {
      item.startIndex = i
      i += item.subColumns.length - 1
      colums.push(...dealColData(item.subColumns, row + 1, item))
    } else {
      item.startIndex = i
      colums.push(defaultsDeep({}, item, parentItem))
    }
    i++
  }
  stackRows.value = Math.max(stackRows.value, row)
  return colums
}

const setData = (
  col:
    | ColumnItem[]
    | {
        columns: ColumnItem[]
        data: { [key: string]: string | number }[]
        compareData?: { [key: string]: string | number }[]
        compareRule?: (row: any, compareData: any) => boolean
        active?: number | number[]
        sum?: SumItem[]
        sumWithScroll?: boolean
      },
  d?: { [key: string]: string | number }[],
  active?: number | number[],
  sum?: SumItem[],
) => {
  componentLoading.value = false
  // stackRows.value = 1
  //console.log("setData", col, d)
  if (Array.isArray(col) && d) {
    headerCol.value = col
    const tempCol = dealColData(col, 1)
    // console.log("tempCol", tempCol, col)
    columns.value = tempCol
    data.value = d
    if (active !== undefined) {
      activeRow.value = active
    }

    if (sum !== undefined) {
      sumConfig.value = sum
    }
  } else if ("columns" in col && "data" in col) {
    headerCol.value = col.columns
    const tempCol = dealColData(col.columns, 1)
    // console.log("tempCol", tempCol, col)
    columns.value = tempCol
    data.value = col.data
    if (col.compareData) {
      compareData.value = col.compareData
      if (col.compareRule) {
        compareFn.value = col.compareRule
      }
    }
    if (col.active !== undefined) {
      activeRow.value = col.active
    }
    if (col.sum) {
      sumConfig.value = col.sum
    }
    sumWithScroll.value = col.sumWithScroll || false
    if (sumWithScroll.value) {
      data.value.push({
        type: "sum",
      })
    }
  }
  columns.value.forEach(item => {
    if (item.style && item.style.height) {
      rowHeight.value = Number(item.style.height.replace("px", ""))
    }
  })
}

const checkRef = (el: HTMLElement, key: string, row: { [key: string]: any }) => {
  if (el) {
    nextTick(() => {
      if (el.children && el.children.length > 0) {
        const textElement = el.children[0]
        const { offsetWidth, scrollWidth } = textElement as any
        if (offsetWidth < scrollWidth) {
          if (!row.overflow) row.overflow = {}
          row.overflow[key] = true
        }
      }
    })
  }
}

provide("checkRef", checkRef)

defineExpose({ setData, setColums, setActive, loading })
</script>
<style lang="scss">
// 声明一个渐出动画
@keyframes pft-fadding-out {
  0% {
    height: var(--MAX-ROW-ALL-HEIGHT, 32px);
    opacity: 1;
  }
  80% {
    height: var(--MAX-ROW-ALL-HEIGHT, 32px);
    opacity: 0;
  }
  100% {
    height: 0;
    opacity: 0;
  }
}
@keyframes pft-fadding-in {
  0% {
    height: 0;
    opacity: 0;
  }
  80% {
    height: 0;
    opacity: 1;
  }
  100% {
    height: var(--MAX-ROW-HEIGHT, 32px);
    opacity: 1;
  }
}
.pft-table-component {
  display: grid;
  width: 100%;
  height: auto;
  grid-template-rows: var(--MAX-TITLE-HEIGHT, 34px) minmax(0px, auto);
  &.no-header {
    grid-template-rows: minmax(0px, auto);
  }
  .pft-t-body {
    display: flex;
    flex-direction: column;

    width: 100%;
    height: auto;
    overflow-y: overlay; /* 使组件可以滚动 */
    scrollbar-width: none; /* Firefox */
    scrollbar-color: rgba(0, 0, 0, 0.5) transparent; /* Firefox */

    /* Webkit 浏览器（Chrome, Safari） */
    &::-webkit-scrollbar {
      width: 0px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 4px;
      transition: background-color 0.3s;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: rgba(0, 0, 0, 0.7);
    }
    .pft-table-component__row {
      flex-shrink: 0;
      &.fadding-out {
        animation: pft-fadding-out 1s forwards;
        overflow: hidden;
      }
      &.fadding-in {
        animation: pft-fadding-in 1s forwards;
        overflow: hidden;
      }
    }

    .pft-table-component__body__cell__overflow__hiden {
      overflow: hidden;
    }
    .pft-table-component__body__cell {
      font-size: 12px;
      color: #a3a6ad;
      text-align: center;
      margin: 8px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 32px;
      border: var(--content-border);
      position: relative;
      &.last-row {
        height: 0px;
        overflow: hidden;
      }
      &.separated-row {
        background: var(--SEPARATED-BACKGROUND);
      }

      &.align-left {
        text-align: left;
        justify-content: flex-start;
      }
      &.align-right {
        text-align: right;
        justify-content: flex-end;
      }
      &.dot {
        padding-left: 14px;
        position: relative;
        &::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          width: 8px;
          height: 8px;
          // background-color: #2bb7f7;
          background-color: #8d9095;
          border-radius: 50%;
          transform: translateY(-50%);
        }
      }
      .rank {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        text-align: center;
        line-height: 18px;
        border-radius: 50%;
        background: rgba(76, 145, 246, 0.3);
        font-family:
          Microsoft YaHei,
          Microsoft YaHei;
        font-weight: 400;
        font-size: 12px;
        color: #e5eaf3;
        display: block;
        overflow: hidden;
        &.img {
          background: transparent;
          img {
            width: 18px;
            height: 18px;
          }
        }
      }
      &.active {
        color: #fff;
        font-weight: bold;
        &.dot {
          &::before {
            background-color: #2bb7f7;
          }
        }
      }
      .text {
        width: 10px;
        flex-grow: 1;
        font-size: inherit;
        color: inherit;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
      .upanddown {
        width: 16px;
        height: 16px;
      }
      .hover-item {
        position: absolute;
        top: -100%;
        left: 0;
        background: #1d1e1f;
        border-radius: 5px;
        box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.72);
        border: 1px solid #363637;
        z-index: 1;
        // display: none;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 12px;
        line-height: 14px;
        transform: translate(0, -35px);
        opacity: 0;
        z-index: -1;
        transition: all 0.3s;
        padding: 10px 15px;
        .hover-arrow {
          position: absolute;
          left: 20px;
          top: 100%;

          &::before {
            border-top-color: transparent !important;
            border-left-color: transparent !important;
            border-top-left-radius: 2px;
            right: 0;
            background: #1d1e1f;
            border: 1px solid #414243;
            box-sizing: border-box;
            content: " ";
            transform: translateY(-50%) rotate(45deg);
            height: 10px;
            position: absolute;
            width: 10px;
            z-index: -1;
          }
        }
      }
      &:hover {
        .hover-item {
          opacity: 1;
          z-index: 2;
        }
      }
    }
  }
  &__header {
    display: grid;
    grid-template-columns: var(--GRID-ROW-TEMPLATE-COLUMNS);
  }
  &__header__item {
    font-size: 14px;
    color: #afe5ff;
    display: flex;
    align-items: center;
    justify-content: center;
    border: var(--content-border);
    // line-height: 32px;
    // height: 32px;
    &.align-left {
      text-align: left;
      justify-content: flex-start;
    }
    &.align-right {
      text-align: right;
      justify-content: flex-end;
    }
  }
}
.table-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: #a3a6ada4;
  font-size: 14px;
  border-radius: 18px;
  box-sizing: border-box;
  padding: 25px 10px;
}
.pft-text-tpl {
  font-family:
    Segoe UI,
    Segoe UI;
  font-weight: 400;
  font-size: 12px;
  color: #a3a6ad;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  .strong {
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: bold;
    font-size: 14px;
    color: #2bb7f7;
  }
}
</style>
